import React from "react";
import { useRequireTenantType } from "@shared/hooks/useRequireTenantType";

interface RequireTenantTypeProps {
  allowedTypes: string[];
  redirectTo?: string;
  children: React.ReactNode;
  loaderComponent?: React.ReactNode;
}

/**
 * Wrapper component that restricts page access based on user's TenantType
 * Shows a loader while validating or if access is denied, then redirects
 * Only renders children when access is explicitly confirmed (hasAccess === true)
 * 
 * @param allowedTypes - Array of TenantType values that are allowed to access the page
 * @param redirectTo - Path to redirect to if user doesn't have required type (default: "/")
 * @param children - The content to render if user has access
 * @param loaderComponent - Optional custom loader component (defaults to standard loader)
 * 
 * @example
 * <RequireTenantType allowedTypes={["PARTNER", "GLOBAL_ADMIN"]}>
 *   <YourPageContent />
 * </RequireTenantType>
 */
export function RequireTenantType({
  allowedTypes,
  redirectTo = "/",
  children,
  loaderComponent,
}: RequireTenantTypeProps) {
  const { isAllowed } = useRequireTenantType(allowedTypes, redirectTo);

  // Show loader if tenantType is not loaded yet or if user doesn't have access
  // Only render children when isAllowed === true (explicit confirmation)
  if (!isAllowed) {
    if (loaderComponent) {
      return <>{loaderComponent}</>;
    }

    // Default loader
    return (
      <div
        className="
          fixed
          inset-0
          flex flex-col items-center justify-center
          bg-black/50
          z-[9999]
        "
      >
        <h2 className="text-xl font-semibold mb-4 text-white">
          Loading...
        </h2>
        <div
          className="
            animate-spin
            rounded-full
            h-12 w-12
            border-4
            border-gray-300
            border-t-blue-500
          "
        />
      </div>
    );
  }

  // User has access confirmed (isAllowed === true), render children
  return <>{children}</>;
}
